import React, { useState, useEffect } from 'react';
import { paymentApi, hospitalApi } from '../services/api';

interface Payment {
  paymentId: string;
  hospitalId: string;
  hospitalName?: string;
  amount: number;
  paymentDate: string;
  notes?: string;
  createdAt: string;
}

interface Hospital {
  hospitalId: string;
  name: string;
  subscriptionAmount: number;
  isActive: boolean;
}

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    hospitalId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [filterHospitalId, setFilterHospitalId] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [paymentsData, hospitalsData] = await Promise.all([
        paymentApi.getAll(),
        hospitalApi.getAll(),
      ]);

      // Create hospital lookup map
      const hospitalMap = new Map(hospitalsData.map((h: Hospital) => [h.hospitalId, h.name]));
      
      // Add hospital names to payments
      const paymentsWithNames = paymentsData.map((p: Payment) => ({
        ...p,
        hospitalName: hospitalMap.get(p.hospitalId) || 'Unknown Hospital',
      }));

      setPayments(paymentsWithNames);
      setHospitals(hospitalsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-fill amount based on hospital subscription
    if (name === 'hospitalId' && value) {
      const selectedHospital = hospitals.find(h => h.hospitalId === value);
      if (selectedHospital) {
        setFormData(prev => ({
          ...prev,
          hospitalId: value,
          amount: selectedHospital.subscriptionAmount.toString(),
        }));
      }
    }
  };

  const openAddModal = () => {
    setFormData({
      hospitalId: '',
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.hospitalId || !formData.amount || !formData.paymentDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      
      // Extract month from payment date (YYYY-MM format)
      const month = formData.paymentDate.substring(0, 7);
      
      await paymentApi.create({
        hospitalId: formData.hospitalId,
        amount: parseFloat(formData.amount),
        month: month,
        paymentDate: formData.paymentDate,
        idempotencyKey: crypto.randomUUID(),
        notes: formData.notes || undefined,
      });
      
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error('Error creating payment:', err);
      alert('Failed to record payment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Parse date string as local time to avoid timezone shift
    // "2026-01-01" should show as "Jan 1, 2026" not "Dec 31, 2025"
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDeletePayment = async (payment: Payment) => {
    if (!window.confirm(`Delete payment of ${formatCurrency(payment.amount)} for ${payment.hospitalName}? This will also subtract this amount from the hospital's total paid.`)) {
      return;
    }

    try {
      setDeleting(payment.paymentId);
      await paymentApi.delete(payment.paymentId);
      loadData(); // Reload to update totals
    } catch (err) {
      console.error('Error deleting payment:', err);
      alert('Failed to delete payment. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  // Filter payments by hospital
  const filteredPayments = filterHospitalId
    ? payments.filter(p => p.hospitalId === filterHospitalId)
    : payments;

  // Calculate totals
  const totalAmount = filteredPayments.reduce((sum, p) => sum + p.amount, 0);
  const currentYear = new Date().getFullYear();
  const yearlyAmount = filteredPayments
    .filter(p => {
      // Parse year from paymentDate string to avoid timezone issues
      const year = parseInt(p.paymentDate.split('-')[0], 10);
      return year === currentYear;
    })
    .reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="payments-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payments-page">
      <div className="page-header">
        <div>
          <h2>Payment Records</h2>
          <p className="page-subtitle">Track and manage monthly subscription payments</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={loadData}>
            🔄 Refresh
          </button>
          <button className="btn-primary" onClick={openAddModal}>
            + Record Payment
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={loadData}>Try Again</button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="payments-summary">
        <div className="summary-card">
          <span className="summary-label">Total Payments</span>
          <span className="summary-value">{formatCurrency(totalAmount)}</span>
          <span className="summary-description">All time</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">{currentYear} Total</span>
          <span className="summary-value">{formatCurrency(yearlyAmount)}</span>
          <span className="summary-description">This year</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Payment Count</span>
          <span className="summary-value">{filteredPayments.length}</span>
          <span className="summary-description">Total records</span>
        </div>
      </div>

      {/* Filter */}
      <div className="filter-section">
        <label htmlFor="filterHospital">Filter by Hospital:</label>
        <select
          id="filterHospital"
          value={filterHospitalId}
          onChange={(e) => setFilterHospitalId(e.target.value)}
        >
          <option value="">All Hospitals</option>
          {hospitals.map((hospital) => (
            <option key={hospital.hospitalId} value={hospital.hospitalId}>
              {hospital.name}
            </option>
          ))}
        </select>
      </div>

      {/* Payments Table */}
      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Hospital</th>
              <th>Amount</th>
              <th>Notes</th>
              <th>Recorded</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-row">
                  <div className="empty-state">
                    <p>No payments recorded yet.</p>
                    <button className="btn-primary" onClick={openAddModal}>
                      Record First Payment
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredPayments.map((payment) => (
                <tr key={payment.paymentId}>
                  <td>{formatDate(payment.paymentDate)}</td>
                  <td>
                    <span className="hospital-name">{payment.hospitalName}</span>
                  </td>
                  <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                  <td className="notes-cell">{payment.notes || '-'}</td>
                  <td className="timestamp-cell">{formatDate(payment.createdAt)}</td>
                  <td>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeletePayment(payment)}
                      disabled={deleting === payment.paymentId}
                      title="Delete Payment"
                    >
                      {deleting === payment.paymentId ? '...' : '🗑️'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Payment Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Record Payment</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="hospitalId">Hospital *</label>
                <select
                  id="hospitalId"
                  name="hospitalId"
                  value={formData.hospitalId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a hospital</option>
                  {hospitals
                    .filter(h => h.isActive)
                    .map((hospital) => (
                      <option key={hospital.hospitalId} value={hospital.hospitalId}>
                        {hospital.name} ({formatCurrency(hospital.subscriptionAmount)}/mo)
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="amount">Amount (USD) *</label>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="Enter payment amount"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="paymentDate">Payment Date *</label>
                  <input
                    type="date"
                    id="paymentDate"
                    name="paymentDate"
                    value={formData.paymentDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Optional payment notes"
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
